import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Plus,
  Check,
  RotateCcw,
  Trash2,
  Copy,
  FileText,
  Target,
  Users,
  Star,
  Sparkles,
  GripVertical,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { getMatiereInfo, getSessionTypeInfo, JournalStatusBadge } from "./constants";
import { resizeSession, moveSession } from "../../lib/api";

// Constants
const PIXELS_PER_MINUTE = 2; // Height per minute
const MIN_SESSION_DURATION = 5; // Minimum 5 minutes

/**
 * Parse time string "HH:MM" to minutes from midnight
 */
const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
};

/**
 * Convert minutes from midnight to "HH:MM" string
 */
const minutesToTime = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};

/**
 * Snap minutes to nearest interval
 */
const snapToInterval = (minutes, interval) => {
  return Math.round(minutes / interval) * interval;
};

/**
 * Calculate session position and height based on time
 */
const getSessionStyle = (session, dayStart, pixelsPerMinute) => {
  const startMinutes = timeToMinutes(session.start_time);
  const endMinutes = timeToMinutes(session.end_time);
  const dayStartMinutes = timeToMinutes(dayStart);
  
  const top = (startMinutes - dayStartMinutes) * pixelsPerMinute;
  const height = (endMinutes - startMinutes) * pixelsPerMinute;
  
  return { top, height: Math.max(height, 30) }; // Minimum 30px height
};

/**
 * Resizable Session Card Component
 */
function ResizableSessionCard({
  session,
  dayStart,
  pixelsPerMinute,
  interval,
  onStatusChange,
  onDelete,
  onDuplicate,
  onEdit,
  onGenerateVariants,
  onCreateResource,
  onOpenResource,
  onResize,
  onMove,
  columnWidth,
  columnIndex,
  totalColumns,
  onSelect,
}) {
  const cardRef = useRef(null);
  const [isResizingTop, setIsResizingTop] = useState(false);
  const [isResizingBottom, setIsResizingBottom] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [previewStyle, setPreviewStyle] = useState(null);
  
  const matiereInfo = getMatiereInfo(session.matiere);
  const sessionTypeInfo = getSessionTypeInfo(session.session_type);
  const Icon = matiereInfo?.icon;
  const hasSkills = session.skills && session.skills.length > 0;
  const hasResource = !!session.resource_id;
  const hasDifferentiation = session.has_differentiation;
  const mainSkill = session.main_skill;
  
  const { top, height } = getSessionStyle(session, dayStart, pixelsPerMinute);
  
  // Calculate left position and width for side-by-side sessions
  const width = totalColumns > 1 ? `calc(${100 / totalColumns}% - 4px)` : "calc(100% - 8px)";
  const left = totalColumns > 1 ? `calc(${(columnIndex * 100) / totalColumns}% + 4px)` : "4px";
  
  // Handle resize start (top)
  const handleResizeTopStart = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizingTop(true);
    
    const startY = e.clientY;
    const originalTop = top;
    const originalStartTime = timeToMinutes(session.start_time);
    
    const handleMouseMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const deltaMinutes = deltaY / pixelsPerMinute;
      let newStartMinutes = snapToInterval(originalStartTime + deltaMinutes, interval);
      
      // Don't allow start time to go past end time
      const endMinutes = timeToMinutes(session.end_time);
      if (newStartMinutes >= endMinutes - MIN_SESSION_DURATION) {
        newStartMinutes = endMinutes - MIN_SESSION_DURATION;
      }
      
      // Don't allow before day start
      const dayStartMinutes = timeToMinutes(dayStart);
      if (newStartMinutes < dayStartMinutes) {
        newStartMinutes = dayStartMinutes;
      }
      
      setPreviewStyle({
        top: (newStartMinutes - dayStartMinutes) * pixelsPerMinute,
        height: (endMinutes - newStartMinutes) * pixelsPerMinute,
        newStartTime: minutesToTime(newStartMinutes),
      });
    };
    
    const handleMouseUp = async () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      setIsResizingTop(false);
      
      if (previewStyle?.newStartTime && previewStyle.newStartTime !== session.start_time) {
        await onResize(session.id, { start_time: previewStyle.newStartTime });
      }
      setPreviewStyle(null);
    };
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [session, top, dayStart, pixelsPerMinute, interval, onResize, previewStyle]);
  
  // Handle resize start (bottom)
  const handleResizeBottomStart = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizingBottom(true);
    
    const startY = e.clientY;
    const originalEndTime = timeToMinutes(session.end_time);
    
    const handleMouseMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const deltaMinutes = deltaY / pixelsPerMinute;
      let newEndMinutes = snapToInterval(originalEndTime + deltaMinutes, interval);
      
      // Don't allow end time to go before start time
      const startMinutes = timeToMinutes(session.start_time);
      if (newEndMinutes <= startMinutes + MIN_SESSION_DURATION) {
        newEndMinutes = startMinutes + MIN_SESSION_DURATION;
      }
      
      const dayStartMinutes = timeToMinutes(dayStart);
      setPreviewStyle({
        top: (startMinutes - dayStartMinutes) * pixelsPerMinute,
        height: (newEndMinutes - startMinutes) * pixelsPerMinute,
        newEndTime: minutesToTime(newEndMinutes),
      });
    };
    
    const handleMouseUp = async () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      setIsResizingBottom(false);
      
      if (previewStyle?.newEndTime && previewStyle.newEndTime !== session.end_time) {
        await onResize(session.id, { end_time: previewStyle.newEndTime });
      }
      setPreviewStyle(null);
    };
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [session, dayStart, pixelsPerMinute, interval, onResize, previewStyle]);
  
  const currentStyle = previewStyle || { top, height };
  
  return (
    <div
      ref={cardRef}
      data-testid={`continuous-session-card-${session.id}`}
      className={`absolute rounded-lg border-2 transition-shadow hover:shadow-lg group ${
        isResizingTop || isResizingBottom ? "ring-2 ring-indigo-400 shadow-xl z-30" : "z-10"
      }`}
      style={{
        top: `${currentStyle.top}px`,
        height: `${currentStyle.height}px`,
        left,
        width,
        backgroundColor: `${matiereInfo?.color}15`,
        borderColor: matiereInfo?.color,
        borderLeftWidth: "4px",
      }}
    >
      {/* Top resize handle */}
      <div
        className="absolute -top-1 left-0 right-0 h-3 cursor-ns-resize flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20"
        onMouseDown={handleResizeTopStart}
      >
        <div className="w-12 h-1.5 bg-slate-400 rounded-full" />
      </div>
      
      {/* Color band at top */}
      <div
        className="h-1 w-full rounded-t"
        style={{ backgroundColor: matiereInfo?.color }}
      />
      
      {/* Card content */}
      <div 
        className="p-1.5 cursor-pointer overflow-hidden h-full"
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.(session);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onEdit(session);
        }}
      >
        {/* Header with title and status */}
        <div className="flex items-start justify-between gap-1 mb-0.5">
          <div className="flex items-center gap-1 min-w-0 flex-1">
            {Icon && <Icon className="w-3 h-3 flex-shrink-0" style={{ color: matiereInfo?.color }} />}
            <span className="font-semibold text-[10px] truncate" style={{ color: matiereInfo?.color }}>
              {session.title}
            </span>
          </div>
          <JournalStatusBadge status={session.status} className="text-[8px] px-1 py-0" />
        </div>
        
        {/* Time display */}
        <div className="text-[9px] text-slate-500 mb-0.5">
          {previewStyle?.newStartTime || session.start_time} - {previewStyle?.newEndTime || session.end_time}
        </div>
        
        {/* Badges - only show if enough height */}
        {currentStyle.height > 60 && (
          <div className="flex flex-wrap gap-0.5 mb-0.5">
            <Badge variant="outline" className={`text-[8px] px-1 py-0 ${sessionTypeInfo?.color}`}>
              {sessionTypeInfo?.label}
            </Badge>
            {session.niveau && (
              <Badge variant="outline" className="text-[8px] px-1 py-0">
                {session.niveau.toUpperCase()}
              </Badge>
            )}
          </div>
        )}
        
        {/* Main skill - only show if enough height */}
        {mainSkill && currentStyle.height > 80 && (
          <div className="flex items-center gap-1 text-[9px] text-slate-600">
            <Star className="w-2 h-2 text-yellow-500" />
            <span className="truncate">{mainSkill.label}</span>
          </div>
        )}
        
        {/* Indicators */}
        {currentStyle.height > 50 && (
          <div className="flex items-center gap-1 mt-0.5">
            {hasSkills && <Target className="w-2.5 h-2.5 text-indigo-500" />}
            {hasDifferentiation && <Users className="w-2.5 h-2.5 text-green-500" />}
            {hasResource && <FileText className="w-2.5 h-2.5 text-purple-500" />}
          </div>
        )}
      </div>
      
      {/* Action buttons - show on hover */}
      <div 
        className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 py-0.5 px-1 rounded"
        onClick={(e) => e.stopPropagation()}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => onStatusChange(session.id, "fait")}>
                <Check className="w-3 h-3 text-green-600" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Fait</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => onStatusChange(session.id, "reporte")}>
                <RotateCcw className="w-3 h-3 text-orange-600" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reporter</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => onDuplicate(session.id)}>
                <Copy className="w-3 h-3 text-blue-600" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Dupliquer</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => onDelete(session.id)}>
                <Trash2 className="w-3 h-3 text-red-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Supprimer</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {/* Bottom resize handle */}
      <div
        className="absolute -bottom-1 left-0 right-0 h-3 cursor-ns-resize flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20"
        onMouseDown={handleResizeBottomStart}
      >
        <div className="w-12 h-1.5 bg-slate-400 rounded-full" />
      </div>
    </div>
  );
}

/**
 * Time column with grid lines
 */
function TimeColumn({ dayStart, dayEnd, interval, pixelsPerMinute, headerHeight }) {
  const startMinutes = timeToMinutes(dayStart);
  const endMinutes = timeToMinutes(dayEnd);
  const lines = [];
  
  for (let m = startMinutes; m <= endMinutes; m += interval) {
    const top = (m - startMinutes) * pixelsPerMinute;
    const time = minutesToTime(m);
    const isHour = m % 60 === 0;
    
    lines.push(
      <div
        key={m}
        className={`absolute left-0 right-0 flex items-center ${isHour ? "font-semibold" : "text-slate-400"}`}
        style={{ top: `${top}px` }}
      >
        <span className={`text-[10px] pr-2 ${isHour ? "text-slate-700" : "text-slate-400"}`}>
          {time}
        </span>
      </div>
    );
  }
  
  const totalHeight = (endMinutes - startMinutes) * pixelsPerMinute;
  
  return (
    <div className="relative w-16 flex-shrink-0" style={{ marginTop: `${headerHeight}px`, height: `${totalHeight}px` }}>
      {lines}
    </div>
  );
}

/**
 * Day column with sessions
 */
function DayColumn({
  date,
  dayLabel,
  dayStart,
  dayEnd,
  interval,
  pixelsPerMinute,
  sessions,
  isToday,
  isHoliday,
  holidayName,
  currentTimeMinutes,
  breaks,
  onAddSession,
  onEditSession,
  onStatusChange,
  onDelete,
  onDuplicate,
  onGenerateVariants,
  onCreateResource,
  onOpenResource,
  onResize,
  onMove,
  onSelect,
}) {
  const columnRef = useRef(null);
  const startMinutes = timeToMinutes(dayStart);
  const endMinutes = timeToMinutes(dayEnd);
  const totalHeight = (endMinutes - startMinutes) * pixelsPerMinute;
  
  // Calculate break zones
  const breakZones = useMemo(() => {
    const zones = [];
    Object.entries(breaks || {}).forEach(([key, breakInfo]) => {
      const breakStart = timeToMinutes(breakInfo.start);
      const breakEnd = timeToMinutes(breakInfo.end);
      
      // Only include if break is within day range
      if (breakStart >= startMinutes && breakEnd <= endMinutes && breakStart < breakEnd) {
        zones.push({
          key,
          label: breakInfo.label,
          top: (breakStart - startMinutes) * pixelsPerMinute,
          height: (breakEnd - breakStart) * pixelsPerMinute,
          isLunch: key === "lunch"
        });
      }
    });
    return zones;
  }, [breaks, startMinutes, endMinutes, pixelsPerMinute]);
  
  // Group overlapping sessions for side-by-side display
  const sessionGroups = useMemo(() => {
    if (!sessions.length) return [];
    
    // Sort sessions by start time
    const sorted = [...sessions].sort((a, b) => 
      timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
    );
    
    const groups = [];
    let currentGroup = [];
    let groupEnd = 0;
    
    for (const session of sorted) {
      const sessionStart = timeToMinutes(session.start_time);
      const sessionEnd = timeToMinutes(session.end_time);
      
      if (sessionStart >= groupEnd) {
        // No overlap, start new group
        if (currentGroup.length) groups.push(currentGroup);
        currentGroup = [session];
        groupEnd = sessionEnd;
      } else {
        // Overlap, add to current group
        currentGroup.push(session);
        groupEnd = Math.max(groupEnd, sessionEnd);
      }
    }
    
    if (currentGroup.length) groups.push(currentGroup);
    return groups;
  }, [sessions]);
  
  // Handle click to add session
  const handleColumnClick = useCallback((e) => {
    if (isHoliday) return;
    
    const rect = columnRef.current.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const clickMinutes = Math.floor(clickY / pixelsPerMinute) + startMinutes;
    const snappedMinutes = snapToInterval(clickMinutes, interval);
    
    onAddSession(date, minutesToTime(snappedMinutes));
  }, [date, startMinutes, pixelsPerMinute, interval, isHoliday, onAddSession]);
  
  // Generate grid lines
  const gridLines = useMemo(() => {
    const lines = [];
    for (let m = startMinutes; m <= endMinutes; m += interval) {
      const top = (m - startMinutes) * pixelsPerMinute;
      const isHour = m % 60 === 0;
      
      lines.push(
        <div
          key={m}
          className={`absolute left-0 right-0 border-t ${
            isHour ? "border-slate-300" : "border-slate-100"
          }`}
          style={{ top: `${top}px` }}
        />
      );
    }
    return lines;
  }, [startMinutes, endMinutes, interval, pixelsPerMinute]);
  
  // Current time indicator
  const currentTimeTop = currentTimeMinutes !== null && currentTimeMinutes >= startMinutes && currentTimeMinutes <= endMinutes
    ? (currentTimeMinutes - startMinutes) * pixelsPerMinute
    : null;
  
  return (
    <div className="flex-1 min-w-[150px]">
      {/* Day header */}
      <div
        className={`p-2 text-center rounded-t-lg mb-1 ${
          isHoliday
            ? "bg-gradient-to-br from-orange-100 to-amber-100 border-2 border-orange-300"
            : isToday
              ? "bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg"
              : "bg-slate-50 border border-slate-200"
        }`}
      >
        <div className={`text-xs font-medium ${isHoliday ? "text-orange-600" : isToday ? "text-indigo-100" : "text-slate-400"}`}>
          {dayLabel}
        </div>
        <div className={`text-lg font-bold ${isHoliday ? "text-orange-700" : isToday ? "text-white" : "text-slate-700"}`}>
          {new Date(date).getDate()}
        </div>
        {isHoliday && (
          <Badge className="bg-orange-500 text-[9px] mt-1">{holidayName}</Badge>
        )}
      </div>
      
      {/* Sessions area */}
      <div
        ref={columnRef}
        className={`relative border rounded-lg ${isHoliday ? "bg-orange-50/50" : "bg-white"}`}
        style={{ height: `${totalHeight}px` }}
        onClick={!isHoliday ? handleColumnClick : undefined}
      >
        {/* Grid lines */}
        {gridLines}
        
        {/* Break zones (recreation, lunch) */}
        {!isHoliday && breakZones.map((zone) => (
          <div
            key={zone.key}
            className={`absolute left-1 right-1 rounded pointer-events-none flex items-center justify-center ${
              zone.isLunch 
                ? "bg-gradient-to-r from-amber-100/80 to-orange-100/80 border border-amber-200" 
                : "bg-gradient-to-r from-green-100/80 to-emerald-100/80 border border-green-200"
            }`}
            style={{ 
              top: `${zone.top}px`, 
              height: `${zone.height}px`,
              backgroundImage: zone.isLunch 
                ? "repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(251, 191, 36, 0.1) 5px, rgba(251, 191, 36, 0.1) 10px)"
                : "repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(34, 197, 94, 0.1) 5px, rgba(34, 197, 94, 0.1) 10px)"
            }}
          >
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
              zone.isLunch ? "bg-amber-200/80 text-amber-700" : "bg-green-200/80 text-green-700"
            }`}>
              {zone.label}
            </span>
          </div>
        ))}
        
        {/* Current time indicator */}
        {currentTimeTop !== null && isToday && (
          <div
            className="absolute left-0 right-0 z-30 flex items-center pointer-events-none"
            style={{ top: `${currentTimeTop}px` }}
          >
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse -ml-1" />
            <div className="flex-1 h-0.5 bg-red-500" />
          </div>
        )}
        
        {/* Holiday overlay */}
        {isHoliday && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm text-orange-400 font-medium">{holidayName}</span>
          </div>
        )}
        
        {/* Sessions */}
        {!isHoliday && sessionGroups.map((group, groupIndex) => (
          group.map((session, sessionIndex) => (
            <ResizableSessionCard
              key={session.id}
              session={session}
              dayStart={dayStart}
              pixelsPerMinute={pixelsPerMinute}
              interval={interval}
              columnIndex={sessionIndex}
              totalColumns={group.length}
              onStatusChange={onStatusChange}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              onEdit={(s) => {
                onSelect?.(s);
                onEditSession(s);
              }}
              onGenerateVariants={onGenerateVariants}
              onCreateResource={onCreateResource}
              onOpenResource={onOpenResource}
              onResize={onResize}
              onMove={onMove}
              onSelect={onSelect}
            />
          ))
        ))}
      </div>
    </div>
  );
}

/**
 * Floating Toolbar - Always visible actions for selected session
 */
function FloatingToolbar({
  session,
  onClose,
  onEdit,
  onStatusChange,
  onDelete,
  onDuplicate,
  onGenerateVariants,
  onCreateResource,
  onOpenResource,
}) {
  if (!session) return null;
  
  const matiereInfo = getMatiereInfo(session.matiere);
  const hasResource = !!session.resource_id;
  const hasDifferentiation = session.has_differentiation;
  const Icon = matiereInfo?.icon;
  
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-200">
      <div 
        className="bg-white rounded-xl shadow-2xl border-2 px-4 py-3 flex items-center gap-4"
        style={{ borderColor: matiereInfo?.color }}
      >
        {/* Session info */}
        <div className="flex items-center gap-2 pr-4 border-r">
          {Icon && <Icon className="w-5 h-5" style={{ color: matiereInfo?.color }} />}
          <div>
            <div className="font-medium text-sm truncate max-w-[200px]">{session.title}</div>
            <div className="text-xs text-slate-500">{session.start_time} - {session.end_time}</div>
          </div>
        </div>
        
        {/* Actions */}
        <TooltipProvider delayDuration={100}>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-indigo-100"
                  onClick={() => onEdit(session)}
                >
                  <FileText className="w-4 h-4 text-indigo-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Modifier</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-green-100"
                  onClick={() => { onStatusChange(session.id, "fait"); onClose(); }}
                >
                  <Check className="w-4 h-4 text-green-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Marquer fait</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-orange-100"
                  onClick={() => { onStatusChange(session.id, "reporte"); onClose(); }}
                >
                  <RotateCcw className="w-4 h-4 text-orange-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reporter</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-blue-100"
                  onClick={() => { onDuplicate(session.id); onClose(); }}
                >
                  <Copy className="w-4 h-4 text-blue-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Dupliquer</TooltipContent>
            </Tooltip>
            
            {hasResource ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0 hover:bg-purple-100"
                    onClick={() => onOpenResource(session.resource_id)}
                  >
                    <FileText className="w-4 h-4 text-purple-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Ouvrir la fiche</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0 hover:bg-emerald-100"
                    onClick={() => { onCreateResource(session.id); onClose(); }}
                  >
                    <Plus className="w-4 h-4 text-emerald-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Créer une fiche</TooltipContent>
              </Tooltip>
            )}
            
            {hasResource && session.target_student_ids?.length > 0 && !hasDifferentiation && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0 hover:bg-violet-100"
                    onClick={() => { onGenerateVariants(session.id); onClose(); }}
                  >
                    <Sparkles className="w-4 h-4 text-violet-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Générer variantes</TooltipContent>
              </Tooltip>
            )}
            
            <div className="w-px h-6 bg-slate-200 mx-1" />
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-red-100"
                  onClick={() => { onDelete(session.id); onClose(); }}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Supprimer</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
        
        {/* Close button */}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 ml-2"
          onClick={onClose}
        >
          <X className="w-4 h-4 text-slate-400" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Main Continuous Time Grid Component
 */
export default function ContinuousTimeGrid({
  weekDates,
  sessionsByDate,
  config,
  holidays,
  onAddSession,
  onEditSession,
  onStatusChange,
  onDelete,
  onDuplicate,
  onGenerateVariants,
  onCreateResource,
  onOpenResource,
  onSessionsUpdated,
}) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);
  
  // Get config values with defaults
  const dayStart = config?.morning_start || "08:30";
  const dayEnd = config?.afternoon_end || "16:30";
  const interval = config?.grid_interval || 15; // Default 15 minutes
  const pixelsPerMinute = PIXELS_PER_MINUTE;
  
  // Break times configuration
  const breaks = {
    morningBreak: {
      start: config?.morning_break_start || "10:15",
      end: config?.morning_break_end || "10:30",
      label: "Récréation"
    },
    lunch: {
      start: config?.morning_end || "11:45",
      end: config?.afternoon_start || "13:30",
      label: "Pause méridienne"
    },
    afternoonBreak: {
      start: config?.afternoon_break_start || "15:00",
      end: config?.afternoon_break_end || "15:15",
      label: "Récréation"
    }
  };
  
  const currentTimeMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
  const todayStr = new Date().toISOString().split("T")[0];
  
  // Check if date is holiday
  const getDateStatus = (dateStr) => {
    const publicHoliday = holidays?.public_holidays?.find(h => h.date === dateStr);
    if (publicHoliday) {
      return { isHoliday: true, name: publicHoliday.name };
    }
    
    for (const vac of holidays?.vacations || []) {
      const start = new Date(vac.start);
      const end = new Date(vac.end);
      const current = new Date(dateStr);
      if (current >= start && current <= end) {
        return { isHoliday: true, name: vac.name };
      }
    }
    
    return { isHoliday: false, name: null };
  };
  
  // Handle resize
  const handleResize = async (sessionId, resizeData) => {
    try {
      await resizeSession(sessionId, resizeData);
      toast.success("Durée modifiée");
      onSessionsUpdated?.();
    } catch (err) {
      console.error("Error resizing session:", err);
      toast.error("Erreur lors du redimensionnement");
    }
  };
  
  // Handle move
  const handleMove = async (sessionId, moveData) => {
    try {
      await moveSession(sessionId, moveData);
      toast.success("Séance déplacée");
      onSessionsUpdated?.();
    } catch (err) {
      console.error("Error moving session:", err);
      toast.error("Erreur lors du déplacement");
    }
  };
  
  const visibleDays = weekDates.filter(d => d.isWorking);
  
  // Header height to align time column with day columns
  const HEADER_HEIGHT = 72; // Height of day header (p-2 + text)
  
  return (
    <div className="flex gap-2 overflow-x-auto pb-4">
      {/* Time column */}
      <TimeColumn
        dayStart={dayStart}
        dayEnd={dayEnd}
        interval={interval}
        pixelsPerMinute={pixelsPerMinute}
        headerHeight={HEADER_HEIGHT}
      />
      
      {/* Day columns */}
      {visibleDays.map((dayInfo) => {
        const dateStr = dayInfo.date;
        const isToday = dateStr === todayStr;
        const dateStatus = getDateStatus(dateStr);
        const sessions = sessionsByDate[dateStr] || [];
        
        return (
          <DayColumn
            key={dateStr}
            date={dateStr}
            dayLabel={dayInfo.label}
            dayStart={dayStart}
            dayEnd={dayEnd}
            interval={interval}
            pixelsPerMinute={pixelsPerMinute}
            sessions={sessions}
            isToday={isToday}
            isHoliday={dateStatus.isHoliday}
            holidayName={dateStatus.name}
            currentTimeMinutes={currentTimeMinutes}
            breaks={breaks}
            onAddSession={onAddSession}
            onEditSession={(session) => {
              setSelectedSession(session);
              onEditSession(session);
            }}
            onStatusChange={onStatusChange}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onGenerateVariants={onGenerateVariants}
            onCreateResource={onCreateResource}
            onOpenResource={onOpenResource}
            onResize={handleResize}
            onMove={handleMove}
            onSelect={setSelectedSession}
          />
        );
      })}
      
      {/* Floating Toolbar */}
      <FloatingToolbar
        session={selectedSession}
        onClose={() => setSelectedSession(null)}
        onEdit={onEditSession}
        onStatusChange={onStatusChange}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onGenerateVariants={onGenerateVariants}
        onCreateResource={onCreateResource}
        onOpenResource={onOpenResource}
      />
    </div>
  );
}
